/**
 * scheduler.js — service layer for triggering/observing the nightly scheduler from Cortex.
 *
 * Talks to /api/run-scheduler (Clerk admin-gated), which fires the GitHub Actions workflow and reads
 * run status. The scheduler runs on GitHub; this is just the in-app control + status. First piece of
 * the Phase 4 "Pulse" surface.
 *
 * Components must call through here, never fetch /api/run-scheduler directly.
 */

async function post(body) {
  const token = (typeof window !== 'undefined' && window.__clerkGetToken) ? await window.__clerkGetToken() : null
  const res = await fetch('/api/run-scheduler', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify(body),
  })
  const data = await res.json().catch(() => ({}))
  if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`)
  return data
}

/** Kick off a scheduler run. mode: 'read_only' (dry, default) | 'live'. testProject: exact name or '' for firm-wide. */
export function triggerSchedulerRun({ mode = 'read_only', testProject = '' } = {}) {
  return post({ action: 'dispatch', mode, testProject })
}

/** Recent workflow runs: [{ id, status, conclusion, event, title, created_at, html_url }]. */
export async function getSchedulerRuns() {
  const { runs } = await post({ action: 'status' })
  return runs || []
}

/** Structured nightly-run history (schedule_runs): totals + summary + payload (briefing_data). */
export async function getSchedulerRunHistory(limit = 10) {
  const { runs } = await post({ action: 'history', limit })
  return runs || []
}

/** Fire the daily practice-performance snapshot job (workload_snapshots / project_snapshots). */
export function triggerSnapshot() {
  return post({ action: 'snapshot' })
}

/** Backfill historical snapshots (reconstructs hours + completions per past weekday). days: lookback. */
export function triggerBackfill(days = 90) {
  return post({ action: 'backfill', days })
}

/** The tunable scheduler/workload levers (scheduler_config). → [{key,label,value,grp,unit,description,applies_to}]. */
export async function getSchedulerConfig() {
  const { levers } = await configPost({ action: 'list' })
  return levers || []
}

/** Update one lever's value. */
export async function saveSchedulerLever(key, value) {
  return configPost({ action: 'update', key, value })
}

async function configPost(body) {
  const token = (typeof window !== 'undefined' && window.__clerkGetToken) ? await window.__clerkGetToken() : null
  const res = await fetch('/api/scheduler-config', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify(body),
  })
  const data = await res.json().catch(() => ({}))
  if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`)
  return data
}

/** Ask Claude about the schedule (read-only). messages: [{role:'user'|'assistant', content}]. */
export async function askSchedule(messages) {
  const token = (typeof window !== 'undefined' && window.__clerkGetToken) ? await window.__clerkGetToken() : null
  const res = await fetch('/api/schedule-chat', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify({ messages }),
  })
  const data = await res.json().catch(() => ({}))
  if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`)
  return data.reply || ''
}
